import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Layout } from "src/components/layout"
import {
  EMPLOYMENT_TYPE,
  Genders,
  PROVINCES,
  SIDE_MENU,
  SNACKBAR_TYPES,
  STATUS,
  USER_TYPE,
} from "src/modules/const"
import { useNavigate } from "react-router"
import dayjs, { Dayjs } from "dayjs"
import InputDateField from "src/components/atom/InputDateField"
import { validateField, validateFields } from "src/modules/validation"
import { useAuth } from "src/providers/LoginProvider"
import * as yup from "yup"
import { AddressService, Tag, TagService, UserService } from "src/openapi"
import { ShowToast } from "../../states/showToast"
import { ShowProgress } from "../../states/showProgress"
import { useRecoilState } from "recoil"
import MultiSelectField from "src/components/atom/MultiSelectField"
import BreadCrumb from "src/components/atom/BreadCrumb"
import { Top } from "../top"
import { SelectDataType } from "src/components/atom/SelectDataType"
import { Button } from "src/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "src/components/ui/card"
import { Label } from "src/components/ui/label"
import { Input } from "src/components/ui/input"
import { Checkbox } from "src/components/ui/checkbox"
import { RadioGroup, RadioGroupItem } from "src/components/ui/radio-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select"

export const MyPageEdit = () => {
  const { t } = useTranslation("translation", { keyPrefix: "employee" })
  const { user } = useAuth()
  const isUserTypeEmployee = user?.type === USER_TYPE.USER

  const [firstName, setFirstName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [firstNameKana, setFirstNameKana] = useState<string>("")
  const [lastNameKana, setLastNameKana] = useState<string>("")
  const [gender, setGender] = useState<string>(Genders.MALE)
  const [birthday, setBirthday] = useState<Dayjs | null>(null)
  const [mailAddress, setMailAddress] = useState<string>("")
  const [postCode, setPostCode] = useState<string>("")
  const [province, setProvince] = useState<string>("none")
  const [city, setCity] = useState<string>("")
  const [street, setStreet] = useState<string>("")
  const [building, setBuilding] = useState<string>("")
  const [phoneNumber, setPhoneNumber] = useState<string>("")
  const [joinDate, setJoinDate] = useState<Dayjs>(dayjs(new Date()))
  const [leaveDate, setLeaveDate] = useState<Dayjs | null>(null)
  const [employmentType, setEmploymentType] = useState<string>("none")
  const [tagValues, setTagValues] = useState<number[]>([])
  const [holding, setHolding] = useState<boolean>(false)
  const [otherGender, setOtherGender] = useState<string>("")
  const [otherGenderDisabled, setOtherGenderDisabled] = useState<boolean>(true)
  const [validation, setValidation] = useState<boolean>(false)

  const navigate = useNavigate()

  const [tags, setTags] = useState<Tag[]>([])

  useEffect(() => {
    if (user == null) {
      return
    }
    TagService.listTags(user.token, Number(user.companyID))
      .then((res) => {
        setTags(res)
      })
      .catch(() => {
        setShowProgress(false)
      })
  }, [user])

  const tagSelectFields: SelectDataType[] = tags.map((tag) => {
    return {
      value: tag.id,
      label: tag.tag,
    }
  })

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [nameKanaValidationMessage, setNameKanaValidationMessage] = useState<string>("")
  const [emailValidationMessage, setEmailValidationMessage] = useState<string>("")
  const [postCodeValidationMessage, setPostCodeValidationMessage] = useState<string>("")
  const [phoneValidationMessage, setPhoneValidationMessage] = useState<string>("")

  const handlePostCodeInput = () => {
    if (!user) {
      return
    }
    if (!postCode) {
      return
    }

    AddressService.autoCompleteAddressByZipCode(postCode, user.token)
      .then((res) => {
        if (res.prefecture) {
          setProvince(res.prefecture as string)
        }

        if (res.city) {
          let city = res.city!
          if (res.townCandidates && res.townCandidates.length >= 1) {
            city = `${city}${res.townCandidates[0]}`
          }
          setCity(city)
        }
      })
      .catch(() => {})
  }

  const getSafeString = (value: string) => {
    if (value == null) return ""
    return value
  }

  const handleGetEmployee = async () => {
    if (!user) {
      return
    }
    setShowProgress(true)
    UserService.getUser(user.token, user.companyID, user.id)
      .then((res) => {
        setShowProgress(false)
        setLastName(res.lastName!)
        setFirstName(res.firstName!)
        setLastNameKana(res.lastNameKana!)
        setFirstNameKana(res.firstNameKana!)
        setGender(res.gender!)
        setOtherGender(getSafeString(res.genderComment!))
        setOtherGenderDisabled(res.gender != Genders.OTHER)
        setBirthday(dayjs(res.birthday!))
        setMailAddress(res.email!)
        setPostCode(getSafeString(res.postCode!))
        setProvince(res.province == null ? "none" : res.province)
        setCity(getSafeString(res.city!))
        setStreet(getSafeString(res.address!))
        setBuilding(getSafeString(res.buildingNo!))
        setPhoneNumber(getSafeString(res.phoneNumber!))
        res.joiningDate && setJoinDate(dayjs(res.joiningDate))
        res.resignDate && setLeaveDate(dayjs(res.resignDate))
        setHolding(res.status == STATUS.OFFICE ? true : false)
        setEmploymentType(res.employmentType!)
        setTagValues(res.tagIds!)
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_detail"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const checkValidation = async () => {
    let valid = true
    valid = valid && lastName != ""
    valid = valid && firstName != ""
    valid = valid && mailAddress != ""

    try {
      await validateFields(
        yup.object().shape({
          firstNameKana: yup.string().matches(/^[\u30A0-\u30FF]*$/, t("please_input_kana_string")),
          lastNameKana: yup.string().matches(/^[\u30A0-\u30FF]*$/, t("please_input_kana_string")),
        }),
        ["firstNameKana", "lastNameKana"],
        [firstNameKana, lastNameKana]
      )
      setNameKanaValidationMessage("")
    } catch (err: any) {
      setNameKanaValidationMessage(err.message)
      valid = valid && false
    }

    try {
      await validateField(
        yup.object().shape({
          mailAddress: yup.string().email(t("please_input_correct_email")),
        }),
        "mailAddress",
        mailAddress
      )
      setEmailValidationMessage("")
    } catch (err: any) {
      setEmailValidationMessage(err.message)
      valid = valid && false
    }

    if (postCode != "") {
      try {
        await validateField(
          yup.object().shape({
            postCode: yup
              .string()
              .max(7, t("postcode_length_max"))
              .matches(/〒?[0-9]{7}/g, t("please_input_correct_post_code")),
          }),
          "postCode",
          postCode
        )
        setPostCodeValidationMessage("")
      } catch (err: any) {
        setPostCodeValidationMessage(err.message)
        valid = valid && false
      }
    }

    if (phoneNumber != "") {
      try {
        await validateField(
          yup.object().shape({
            phoneNumber: yup
              .string()
              .matches(
                /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im,
                t("please_input_correct_phone_number")
              ),
          }),
          "phoneNumber",
          phoneNumber
        )
        setPhoneValidationMessage("")
      } catch (err: any) {
        setPhoneValidationMessage(err.message)
        valid = valid && false
      }
    }

    setValidation(valid)
  }

  const handleUpdate = () => {
    if (!user) {
      return
    }
    setShowProgress(true)

    UserService.updateUser(
      user.companyID,
      user.id,
      {
        lastName: lastName,
        firstName: firstName,
        lastNameKana: lastNameKana,
        firstNameKana: firstNameKana,
        gender: gender,
        genderComment: otherGender,
        birthday: birthday?.isValid() ? (birthday?.format("YYYY-MM-DD") as string) : undefined,
        postCode: postCode,
        province: province == "none" ? undefined : province,
        city: city,
        address: street,
        buildingNo: building,
        phoneNumber: phoneNumber,
        joiningDate: joinDate.format("YYYY-MM-DD"),
        resignDate: leaveDate?.isValid() ? (leaveDate?.format("YYYY-MM-DD") as string) : undefined,
        status: holding ? STATUS.OFFICE : STATUS.RESIGNED,
        userType: user.type,
        employmentType: employmentType == "none" ? undefined : employmentType,
        tagIds: tagValues,
      },
      user.token
    )
      .then(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("success_update"), type: SNACKBAR_TYPES.SUCCESS })
        navigate("/mypage")
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_update"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  useEffect(() => {
    if (user == null) {
      return
    }
    handleGetEmployee()
  }, [user])

  useEffect(() => {
    if (holding) {
      setLeaveDate(null)
    }
  }, [holding])

  if (!user) {
    return <Top />
  }

  return (
    <Layout menu={SIDE_MENU.MY_PAGE}>
      <BreadCrumb
        paths={[
          {
            name: t("edit_title"),
            link: "",
          },
        ]}
      />
      <Card className="m-4 lg:m-6">
        <CardHeader>
          <CardTitle>{t("edit_title")}</CardTitle>
          <CardDescription>{t("edit_description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label className="text-sm font-medium after:content-['*'] after:ml-0.5 after:text-red-500">
              {t("name")}
            </Label>
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value)
                  checkValidation()
                }}
                placeholder={t("last_name")}
                className="w-full sm:w-64"
              />
              <Input
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value)
                  checkValidation()
                }}
                placeholder={t("first_name")}
                className="w-full sm:w-64"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium after:content-['*'] after:ml-0.5 after:text-red-500">
              {t("name_kana")}
            </Label>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="space-y-1">
                <Input
                  value={lastNameKana}
                  onChange={(e) => {
                    setLastNameKana(e.target.value)
                    checkValidation()
                  }}
                  placeholder={t("last_name_kana")}
                  className="w-full sm:w-64"
                />
                {nameKanaValidationMessage && (
                  <p className="text-xs text-red-500">{nameKanaValidationMessage}</p>
                )}
              </div>
              <Input
                value={firstNameKana}
                onChange={(e) => {
                  setFirstNameKana(e.target.value)
                  checkValidation()
                }}
                placeholder={t("first_name_kana")}
                className="w-full sm:w-64"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t("gender")}</Label>
            <div className="flex flex-col sm:flex-row gap-4">
              <RadioGroup
                value={gender}
                onValueChange={(value) => {
                  setGender(value)
                  if (value === Genders.MALE || value === Genders.FEMALE) {
                    setOtherGender("")
                    setOtherGenderDisabled(true)
                  } else {
                    setOtherGenderDisabled(false)
                  }
                  checkValidation()
                }}
                className="flex flex-row gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value={Genders.MALE} id="male" />
                  <Label htmlFor="male" className="font-normal cursor-pointer">
                    {t("male")}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value={Genders.FEMALE} id="female" />
                  <Label htmlFor="female" className="font-normal cursor-pointer">
                    {t("female")}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value={Genders.OTHER} id="other" />
                  <Label htmlFor="other" className="font-normal cursor-pointer">
                    {t("other")}
                  </Label>
                </div>
              </RadioGroup>
              <Input
                disabled={otherGenderDisabled}
                value={otherGender}
                onChange={(e) => setOtherGender(e.target.value)}
                className="w-full sm:w-64"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t("birthday")}</Label>
            <InputDateField
              date={birthday}
              handleDate={setBirthday}
              inputStyle={{ width: "200px", height: "40px" }}
              checkValidation={checkValidation}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t("email_address")}</Label>
            <div className="space-y-1">
              <Input
                value={mailAddress}
                onChange={(e) => {
                  setMailAddress(e.target.value)
                  checkValidation()
                }}
                placeholder={t("email_address_placeholder")}
                className="w-full sm:w-64"
                disabled
              />
              {emailValidationMessage && (
                <p className="text-xs text-red-500">{emailValidationMessage}</p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t("post_code")}</Label>
            <div className="space-y-1">
              <Input
                value={postCode}
                onChange={(e) => {
                  setPostCode(e.target.value)
                  checkValidation()
                }}
                onBlur={handlePostCodeInput}
                placeholder={t("post_code_placeholder")}
                className="w-full sm:w-64"
              />
              <p className="text-xs text-gray-500">{t("not_input_hypen")}</p>
              {postCodeValidationMessage && (
                <p className="text-xs text-red-500">{postCodeValidationMessage}</p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t("address")}</Label>
            <div className="space-y-3">
              <Select value={province} onValueChange={setProvince}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder={t("province")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("please_select")}</SelectItem>
                  {PROVINCES.filter((p) => p.value !== "none").map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={city}
                onChange={(e) => {
                  setCity(e.target.value)
                  checkValidation()
                }}
                placeholder={t("city")}
                className="w-full sm:w-64"
              />
              <Input
                value={street}
                onChange={(e) => {
                  setStreet(e.target.value)
                  checkValidation()
                }}
                placeholder={t("street")}
                className="w-full sm:w-64"
              />
              <Input
                value={building}
                onChange={(e) => {
                  setBuilding(e.target.value)
                  checkValidation()
                }}
                placeholder={t("building")}
                className="w-full sm:w-64"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t("phone_number")}</Label>
            <div className="space-y-1">
              <Input
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value)
                  checkValidation()
                }}
                placeholder={t("phone_number_placeholder")}
                className="w-full sm:w-64"
              />
              <p className="text-xs text-gray-500">{t("not_input_hypen")}</p>
              {phoneValidationMessage && (
                <p className="text-xs text-red-500">{phoneValidationMessage}</p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t("join_company_date")}</Label>
            <InputDateField
              date={joinDate}
              handleDate={setJoinDate}
              inputStyle={{ width: "200px", height: "40px" }}
              checkValidation={checkValidation}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t("leave_company_date")}</Label>
            <div className="flex flex-col sm:flex-row gap-4">
              <InputDateField
                date={leaveDate}
                handleDate={setLeaveDate}
                inputStyle={{ width: "200px", height: "40px" }}
                checkValidation={checkValidation}
                disabled={holding || isUserTypeEmployee}
              />
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="holding"
                  checked={holding}
                  onCheckedChange={(checked) => setHolding(checked as boolean)}
                  disabled={isUserTypeEmployee}
                />
                <Label htmlFor="holding" className="font-normal cursor-pointer">
                  {t("in_holding")}
                </Label>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t("employment_type")}</Label>
            <Select
              value={employmentType}
              onValueChange={setEmploymentType}
              disabled={isUserTypeEmployee}
            >
              <SelectTrigger className="w-full sm:w-64" disabled={isUserTypeEmployee}>
                <SelectValue placeholder={t("please_select")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("please_select")}</SelectItem>
                {EMPLOYMENT_TYPE.filter((e) => e.value !== "none").map((e) => (
                  <SelectItem key={e.value} value={e.value}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t("tag")}</Label>
            <div className="w-full sm:w-[300px]">
              <MultiSelectField
                sx={{ width: "100%", height: "auto" }}
                inputStyle={{ width: "100%" }}
                datas={tagSelectFields}
                selected={tagValues}
                handleSelected={setTagValues}
                placeholder={t("please_select")}
                checkValidation={checkValidation}
                disabled={isUserTypeEmployee}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row gap-3 px-4 lg:px-6 pb-6">
        <Button
          className="bg-[#00c298] hover:bg-[#00a080] text-white w-full sm:w-48"
          disabled={!validation}
          onClick={() => handleUpdate()}
        >
          {t("edit_button")}
        </Button>
        <Button
          variant="outline"
          className="border-[#00c298] text-[#00c298] hover:bg-[#00c298]/10 w-full sm:w-48"
          onClick={() => {
            navigate(`/mypage`)
          }}
        >
          {t("back")}
        </Button>
      </div>
    </Layout>
  )
}
